import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import type { ScheduledWorkflow } from "@/reducers/openclawWorkflowsSlice"
import { formatSchedule } from "@/lib/formatWorkflowSchedule"
import {
  deleteCronAutomation,
  setCronAutomationStatus,
} from "@/api/xeps/CronAutomations"
import { CRON_GONE, useAutomationMutation } from "@/hooks/useAutomationMutation"

export type AutomationDetailsDialogProps = {
  /** The rule being viewed; null closes the dialog. */
  job: ScheduledWorkflow | null
  onOpenChange: (open: boolean) => void
}

/**
 * Read-only view of a personal rule this panel has no edit form for (a
 * geofence trigger, a fuel-norm event) — there's nowhere to build the zone
 * picker these need, but the dispatcher who created one should still be able
 * to see it and remove it without going back to chat.
 */
export const AutomationDetailsDialog: React.FC<
  AutomationDetailsDialogProps
> = ({ job, onOpenChange }) => {
  const { t } = useTranslation()
  const { saving, error, setError, run } = useAutomationMutation(CRON_GONE)

  useEffect(() => {
    if (job) setError(null)
  }, [job, setError])

  const remove = async () => {
    if (!job) return
    const ok = await run((client) => deleteCronAutomation(client, job.id))
    if (ok) onOpenChange(false)
  }

  const toggleStatus = async () => {
    if (!job) return
    await run((client) =>
      setCronAutomationStatus(
        client,
        job.id,
        job.status === "active" ? "paused" : "active",
      ),
    )
  }

  return (
    <Dialog open={job !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="truncate">{job?.name}</DialogTitle>
          <DialogDescription>
            {job && formatSchedule(job.schedule, job.status, t)
              ? t("ai.detailsDialog.triggers", {
                  schedule: formatSchedule(job.schedule, job.status, t),
                })
              : t("ai.detailsDialog.createdFromChat")}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-xs text-red-500" role="alert">
            {error}
          </p>
        )}

        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            className="text-red-500 hover:text-red-600"
            disabled={saving}
            onClick={remove}
          >
            {t("common.delete")}
          </Button>
          <Button variant="outline" disabled={saving} onClick={toggleStatus}>
            {job?.status === "active"
              ? t("ai.automations.pause")
              : t("ai.automations.resume")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AutomationDetailsDialog
